import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { APP_CONFIG } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import HeaderView from "../../../components/admin/headerView";
import ListingTableHeader from "../../../components/listingTableHeader";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import AdminTable from "../../../components/adminTable";
import { TAdminTableRowData } from "../../../components/types";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import CreateCustomIconModal from "./modalAddCustomIcon";
import { useGetCustomAssetImageListQuery } from "../../../api/network/adminApiServices";

const CustomIconsListing: React.FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.import_export",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listingWrapperRef = useRef<HTMLDivElement>(null);

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  const [search, setSearch] = useState("");
  const [filteredResults, setFilteredResults] = useState<any[] | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const { data: iconsData, isLoading } = useGetCustomAssetImageListQuery();

  const allIcons: any[] = iconsData?.results ?? [];

  const displayData = filteredResults ?? allIcons;

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.adminChildren.importExport);
  };

  const debouncedSearch = useDebouncedCallback((value: string) => {
    if (!value.trim()) {
      setFilteredResults(null);
    } else {
      const filtered = allIcons.filter((item) =>
        item.image_url.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredResults(filtered);
    }
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleCopy = (text: string, itemId: number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(itemId);
    setTimeout(() => setCopiedId(null), 1000);
  };

  const columns = ["Image", "URL", "Actions"];

  const tableData: TAdminTableRowData[] = displayData.map((item) => ({
    navLink: "",
    cellData: [
      <div key={`img-${item.id}`} className="flex items-center justify-start">
        <img
          src={item.image_url}
          alt={item.file_name}
          className="size-10 object-contain rounded"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      </div>,
      <div key={`url-${item.id}`} className="flex items-center gap-1">
        <span className="font-mono text-sm text-gray-700  max-w-[200px] block">
          {item.copy_image_url}
        </span>
      </div>,
      <div key={`action-${item.id}`} className="flex items-center">
        <button
          onClick={(e) => {
            e.preventDefault();
            handleCopy(item.copy_image_url, item.id);
          }}
          className="text-primary-like hover:text-blue-700 font-semibold"
          type="button"
        >
          {copiedId === item.id ? (
            <span className="text-green-600">Copied!</span>
          ) : (
            <span>Copy</span>
          )}
        </button>
      </div>,
    ],
    isActive: true,
  }));

  return (
    <div className="bg-white min-h-screen">
      <HeaderView
        title="Asset Custom Icons"
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />

      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading=""
          buttonText="Add Custom Icon"
          buttonCallback={() => {
            dispatch(
              setModalsData({ ...modalsState, showCreateCustomIcon: true })
            );
          }}
        />

        <ListingTableHeader
          heading="Custom Icon Listing"
          searchBoxPlaceholder={t("search_placeholder") || "Search URLs..."}
          searchBoxValue={search}
          searchBoxOnChange={handleSearchChange}
          searchBoxOnClear={() => {
            setSearch("");
            setFilteredResults(null);
          }}
          filters={[]}
          activeFilterSlug=""
          handleFilterChange={() => {}}
        />

        <div className="py-4 mt-4 relative">
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns}
            data={tableData}
            isLoading={isLoading}
            listingWrapperRef={listingWrapperRef}
          />
        </div>
      </div>
      <CreateCustomIconModal />
    </div>
  );
};

export default CustomIconsListing;
